import { api } from "@/redux/api";

import {
	Analytics,
	AnalyticsRequest,
	AnalyticsSchema,
} from "../../pages/Analytics/schema/analytics";
import {
	AnalyticsOverview,
	AnalyticsOverviewSchema,
	TopCampaigns,
	TopCampaignsSchema,
} from "../../pages/Analytics/schema/overview";

import { SignalAnalytics, SignalAnalyticsSchema } from "../../pages/Analytics/schema/signals";

import { UserActivity, UserActivitySchema } from "../../pages/Analytics/schema/userActivity";

export interface UserActivityRequest {
	pastDays?: number;
	futureDays?: number;
	userIds?: number[];
	/** Narrows the sent/queued counts. Caps and credit spend are not list-scoped. */
	listIds?: number[];
}

export interface SignalAnalyticsRequest {
	months?: number;
	listIds?: number[];
	/** Period bounds. The months are this report's axis, so these move and resize it. */
	from?: string;
	to?: string;
	/** Narrows the outreach columns only — tracked profiles and signals carry no sender. */
	senderUserIds?: number[];
}

export interface TopCampaignsRequest {
	limit?: number;
	minContactedLeads?: number;
}

export const analyticsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getAnalytics: builder.mutation<Analytics, AnalyticsRequest>({
			query: (body) => ({
				url: `/reports`,
				method: "POST",
				body,
				responseSchema: AnalyticsSchema,
			}),
		}),
		getAnalyticsOverview: builder.mutation<AnalyticsOverview, AnalyticsRequest>({
			query: (body) => ({
				url: `/reports/overview`,
				method: "POST",
				body,
				responseSchema: AnalyticsOverviewSchema,
			}),
		}),
		getTopCampaigns: builder.query<TopCampaigns, TopCampaignsRequest>({
			query: (body) => ({
				url: `/reports/top-campaigns`,
				method: "POST",
				body,
				responseSchema: TopCampaignsSchema,
			}),
		}),
		getMonthlySignals: builder.query<SignalAnalytics, SignalAnalyticsRequest>({
			query: (body) => ({
				url: `/reports/signals/monthly`,
				method: "POST",
				body,
				responseSchema: SignalAnalyticsSchema,
			}),
		}),
		getUserActivity: builder.query<UserActivity, UserActivityRequest>({
			query: (body) => ({
				url: `/reports/users/activity`,
				method: "POST",
				body,
				responseSchema: UserActivitySchema,
			}),
		}),
		downloadAnalyticsExport: builder.query<Blob, AnalyticsRequest>({
			query: (body) => ({
				url: `/reports/export`,
				method: "POST",
				body,

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
	}),
});

export const {
	useGetAnalyticsMutation,
	useGetAnalyticsOverviewMutation,
	useGetTopCampaignsQuery,
	useGetMonthlySignalsQuery,
	useGetUserActivityQuery,
	useLazyDownloadAnalyticsExportQuery,
} = analyticsApi;
